/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import "./CreateProject.css";

import ttsService, { IProject, IBook, IClip, ProjectSamplingDefaults } from "../../services/ttsService";
import { listPartners, type IPartner } from "../../services/partnerService";
import type { RootState } from "../../app/store";

import ProjectsView from "./ProjectsView";
import BookEditor from "./BookEditor";
import NewProjectModal from "./NewProjectModal";
import AssignPartnerModal from "./AssignPartnerModal";
import NewBookModal from "./NewBookModal";
import { BookConfig } from "../../utils/types";
import ConfirmModal from "../../components/ConfirmModal";

type View = "projects" | "book-editor";

interface BookClipStats {
  total: number;
  chapters: Record<string, number>;
}

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
}
const CLOSED: ConfirmState = { open: false, title: "", message: "", confirmLabel: "Delete", onConfirm: () => { } };

const CreateProject: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { role } = useSelector((state: RootState) => state.auth);
  const notifications = useSelector((state: RootState) => state.notifications.notifications)
  const isAdmin = role === "admin";

  const [view, setView] = useState<View>("projects");
  const [activeProject, setActiveProject] = useState<IProject | null>(null);
  const [activeBook, setActiveBook] = useState<IBook | null>(null);

  const [projects, setProjects] = useState<IProject[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [booksByProject, setBooksByProject] = useState<Record<string, IBook[]>>({});
  const [loadingBooks, setLoadingBooks] = useState<Record<string, boolean>>({});
  const [clipCountsByBook, setClipCountsByBook] = useState<Record<string, BookClipStats>>({});
  const [mergedCountsByBook, setMergedCountsByBook] = useState<Record<string, number>>({});

  const [showNewProject, setShowNewProject] = useState(false);
  const [editProject, setEditProject] = useState<IProject | null>(null);
  const [assignProject, setAssignProject] = useState<IProject | null>(null);
  const [partners, setPartners] = useState<IPartner[]>([]);
  const [newBookProject, setNewBookProject] = useState<IProject | null>(null);

  const partnerNames = useMemo(
    () => Object.fromEntries(partners.map((p) => [p._id, p.name])),
    [partners],
  );

  // ── Confirm modal ─────────────────────────────────────────────────────────
  const [confirm, setConfirm] = useState<ConfirmState>(CLOSED);

  const askConfirm = (title: string, message: string, confirmLabel: string, onConfirm: () => void) =>
    setConfirm({ open: true, title, message, confirmLabel, onConfirm });

  const closeConfirm = () => setConfirm(CLOSED);

  // ── Load projects ─────────────────────────────────────────────────────────
  useEffect(() => {
    ttsService.getProjects()
      .then((d: any) => { setProjects(d); setLoadingProjects(false); })
      .catch(() => setLoadingProjects(false));
  }, []);



  useEffect(() => {
    if (!isAdmin) return;
    listPartners()
      .then(setPartners)
      .catch(() => setPartners([]));
  }, [isAdmin]);

  /** Loads books + clip/merged counts for one project (used by accordion expand and URL restore). */
  const loadBooksForProject = useCallback(async (projectId: string): Promise<IBook[]> => {
    const books = await ttsService.getBooksForProject(projectId);
    setBooksByProject(p => ({ ...p, [projectId]: books }));

    await Promise.all(books.map(async (b: IBook) => {
      try {
        const [clips, merged] = await Promise.all([
          ttsService.getClipsForBook(b._id),
          ttsService.getMergedForBook(b._id),
        ]);
        const chapters: Record<string, number> = {};
        (clips as IClip[]).forEach((c: IClip) => {
          chapters[c.chapter] = (chapters[c.chapter] || 0) + 1;
        });
        setClipCountsByBook(p => ({ ...p, [b._id]: { total: clips.length, chapters } }));
        setMergedCountsByBook(p => ({ ...p, [b._id]: merged.length }));
      } catch (error) {
        console.log("ERROR------", error);
      }
    }));
    return books;
  }, []);

  const handleExpandProject = useCallback(async (projectId: string) => {
    if (booksByProject[projectId]) return;
    setLoadingBooks(p => ({ ...p, [projectId]: true }));
    try {
      await loadBooksForProject(projectId);
    } catch {
      setBooksByProject(p => ({ ...p, [projectId]: [] }));
    } finally {
      setLoadingBooks(p => ({ ...p, [projectId]: false }));
    }
  }, [booksByProject, loadBooksForProject]);

  const clearEditorFromUrl = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const exitBookEditor = useCallback(() => {
    clearEditorFromUrl();
    setView("projects");
    setActiveProject(null);
    setActiveBook(null);
  }, [clearEditorFromUrl]);

  // ── Create project ────────────────────────────────────────────────────────
  const handleCreateProject = async (
    name: string,
    description: string,
    defaultSampling: ProjectSamplingDefaults,
    partnerId: string | null,
  ) => {
    try {
      const created = await ttsService.createProject({
        projectName: name,
        description,
        defaultSampling,
        partnerId,
      });
      const updated = await ttsService.getProjects();
      const pid = String(created.projectId);
      setProjects(updated.map(p =>
        String(p._id) === pid
          ? {
            ...p,
            partnerId: created.partnerId ?? partnerId,
            defaultSampling: created.defaultSampling ?? p.defaultSampling,
          }
          : p
      ));
    } catch (e) { console.error(e); }
    setShowNewProject(false);
  };

  const handleAssignPartner = async (partnerId: string | null) => {
    if (!assignProject) return;
    try {
      const result = await ttsService.assignProjectPartner(assignProject._id, partnerId);
      setProjects((prev) =>
        prev.map((p) =>
          String(p._id) === String(result.projectId)
            ? { ...p, partnerId: result.partnerId }
            : p,
        ),
      );
    } catch (e) {
      console.error(e);
    }
    setAssignProject(null);
  };

  // ── Edit project ──────────────────────────────────────────────────────────
  const handleEditProject = async (
    name: string,
    description: string,
  ) => {
    if (!editProject) return;
    try {
      await ttsService.updateProject(editProject._id, name, description);
      setProjects(prev => prev.map(p =>
        p._id === editProject._id ? { ...p, projectName: name, description } : p
      ));
    } catch (e) { console.error(e); }
    setEditProject(null);
  };

  // ── Delete project — uses ConfirmModal ────────────────────────────────────
  const handleDeleteProject = (projectId: string, projectName: string) => {
    askConfirm(
      "Delete project",
      `"${projectName}" and all its books, clips and merged chapters will be permanently deleted.`,
      "Delete Project",
      async () => {
        closeConfirm();
        try {
          await ttsService.deleteProject(projectId);
          setProjects(p => p.filter(x => x._id !== projectId));
          setBooksByProject(p => { const n = { ...p }; delete n[projectId]; return n; });
          if (activeProject && String(activeProject._id) === String(projectId)) exitBookEditor();
        } catch (e) { console.error(e); }
      }
    );
  };

  // ── New book modal: refresh project from API so defaultSampling is current ──
  const handleOpenNewBook = useCallback(async (p: IProject) => {
    const pid = String(p._id);
    try {
      const fresh = await ttsService.getProject(pid);
      setNewBookProject(fresh);
      const list = await ttsService.getProjects();
      setProjects(list);
    } catch {
      try {
        const list = await ttsService.getProjects();
        setProjects(list);
        const fresh = list.find(x => String(x._id) === pid);
        setNewBookProject(fresh ?? p);
      } catch {
        setNewBookProject(p);
      }
    }
  }, []);

  // ── Create book ───────────────────────────────────────────────────────────
  const handleCreateBook = async (project: IProject, cfg: BookConfig) => {
    try {
      await ttsService.createBook(project._id, cfg as any);
      const books = await ttsService.getBooksForProject(project._id);
      setBooksByProject(p => ({ ...p, [project._id]: books }));
    } catch (e) { console.error(e); }
    setNewBookProject(null);
  };

  // ── Delete book — uses ConfirmModal ───────────────────────────────────────
  const handleDeleteBook = (bookId: string, projectId: string, bookTitle: string) => {
    askConfirm(
      "Delete book",
      `"${bookTitle}" and all its clips and merged chapters will be permanently deleted.`,
      "Delete Book",
      async () => {
        closeConfirm();
        try {
          await ttsService.deleteBook(bookId);
          setBooksByProject(p => ({
            ...p,
            [projectId]: (p[projectId] || []).filter(b => b._id !== bookId),
          }));
          setClipCountsByBook(p => { const n = { ...p }; delete n[bookId]; return n; });
          if (
            activeBook && activeProject &&
            String(activeBook._id) === String(bookId) &&
            String(activeProject._id) === String(projectId)
          ) exitBookEditor();
        } catch (e) { console.error(e); }
      }
    );
  };

  const handleOpenBook = (project: IProject, book: IBook) => {
    setSearchParams({ project: String(project._id), book: String(book._id) });
    setActiveProject(project);
    setActiveBook(book);
    setView("book-editor");
  };

  // Restore book editor after refresh when ?project=&book= are present
  const urlProjectId = searchParams.get("project") || "";
  const urlBookId = searchParams.get("book") || "";
  const booksCachedForUrlProject = urlProjectId ? booksByProject[urlProjectId] : undefined;

  useEffect(() => {
    if (!urlProjectId || !urlBookId || loadingProjects) return;

    const proj = projects.find(p => String(p._id) === urlProjectId);
    if (!proj) {
      setSearchParams({}, { replace: true });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        let books = booksCachedForUrlProject;
        if (!books) {
          setLoadingBooks(p => ({ ...p, [urlProjectId]: true }));
          try {
            books = await loadBooksForProject(urlProjectId);
          } catch {
            if (!cancelled) setSearchParams({}, { replace: true });
            return;
          } finally {
            if (!cancelled) setLoadingBooks(p => ({ ...p, [urlProjectId]: false }));
          }
        }

        const book = books.find(b => String(b._id) === urlBookId);
        if (!book || cancelled) {
          if (!cancelled) setSearchParams({}, { replace: true });
          return;
        }

        setActiveProject(proj);
        setActiveBook(book);
        setView("book-editor");
      } catch {
        if (!cancelled) setSearchParams({}, { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    urlProjectId,
    urlBookId,
    loadingProjects,
    projects,
    booksCachedForUrlProject,
    loadBooksForProject,
    setSearchParams,
  ]);

  const handleBookUpdated = (updated: IBook) => {
    if (!activeProject) return;
    setActiveBook(updated);
    setBooksByProject(p => ({
      ...p,
      [activeProject._id]: (p[activeProject._id] || []).map(b =>
        b._id === updated._id ? updated : b
      ),
    }));
  };

  const fetchProjects = () => {
    setLoadingProjects(true);
    ttsService.getProjects()
      .then((d: any) => { setProjects(d); setLoadingProjects(false); })
      .catch(() => setLoadingProjects(false));
  };
  useEffect(() => {
    fetchProjects()
  }, [])

  useEffect(() => {
    const latest = notifications[0];
    if (latest?.type === "Assign-Project") {
      fetchProjects();
    }
  }, [notifications?.length]);

  return (
    <div className="cp">

      {/* Confirm modal */}
      {confirm.open && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          onConfirm={confirm.onConfirm}
          onCancel={closeConfirm}
        />
      )}

      {/* Header */}
      <div className="cp-hdr">
        <h1>
          Create Project
        </h1>
        <div className="cp-hdr__right">
          {view === "book-editor" && (
            <button className="cp-tab" style={{ marginLeft: 8 }} onClick={exitBookEditor}>
              ← All Projects
            </button>
          )}
        </div>
      </div>

      {view === "projects" && (
        <ProjectsView
          projects={projects}
          loading={loadingProjects}
          isAdmin={isAdmin}
          partnerNames={partnerNames}
          booksByProject={booksByProject}
          loadingBooks={loadingBooks}
          onExpandProject={handleExpandProject}
          onOpenBook={handleOpenBook}
          onNewBook={handleOpenNewBook}
          onNewProject={() => setShowNewProject(true)}
          onEditProject={setEditProject}
          onAssignPartner={setAssignProject}
          onDeleteProject={(id, name) => handleDeleteProject(id, name)}
          onDeleteBook={(bookId, projectId, title) => handleDeleteBook(bookId, projectId, title)}
          clipCountsByBook={clipCountsByBook}
          mergedCountsByBook={mergedCountsByBook}
        />
      )}

      {view === "book-editor" && activeProject && activeBook && (
        <BookEditor
          project={activeProject}
          book={activeBook}
          onBack={exitBookEditor}
          onUpdate={handleBookUpdated}
        />
      )}

      {showNewProject && (
        <NewProjectModal
          mode="create"
          partners={partners}
          onSave={handleCreateProject}
          onCancel={() => setShowNewProject(false)}
        />
      )}
      {editProject && (
        <NewProjectModal
          mode="edit"
          initialName={editProject.projectName}
          initialDescription={editProject.description}
          partners={partners}
          onSave={(name, description) => handleEditProject(name, description)}
          onCancel={() => setEditProject(null)}
        />
      )}
      {assignProject && (
        <AssignPartnerModal
          project={assignProject}
          partners={partners}
          onSave={handleAssignPartner}
          onCancel={() => setAssignProject(null)}
        />
      )}
      {newBookProject && (
        <NewBookModal
          key={newBookProject._id}
          project={newBookProject}
          onSave={cfg => handleCreateBook(newBookProject, cfg)}
          onCancel={() => setNewBookProject(null)}
        />
      )}
    </div>
  );
};

export default CreateProject;